import { isDefined } from "@omni/utils/helper.utils";
import {
    createContext,
    Dispatch,
    PropsWithChildren,
    SetStateAction,
    use,
    useState
} from "react";

import { SearchItem } from "../StoreSearchInput/storeSearchInput.types";
import { FIXED_STORE_FEATURES } from "./storeSearch.config";
import { setSearchParams, toggleSearchParam } from "./storeSearch.helpers";
import { StoreSearchStore, StoreUrl } from "./storeSearch.types";

type StoreSearchContextValue = {
    selectedStore?: StoreSearchStore;
    stores: StoreSearchStore[];
    storesLoading: boolean;
    storeUrls: Array<StoreUrl>;
    storeFeatures: Array<string>;
    tab: "map" | "list";
    selectedSearchItem?: SearchItem;
    setSearchParams: (
        params: Record<string, string | number>,
        paramsToDelete: Array<string>
    ) => URLSearchParams;
    toggleSearchParam: (param: string, value: string) => URLSearchParams;
    resetSearchItem: () => void;
    setTab: Dispatch<SetStateAction<"list" | "map">>;
};

type StoreSearchContextProviderProps = PropsWithChildren<{
    selectedStore?: StoreSearchStore;
    stores: StoreSearchStore[];
    storesLoading: boolean;
    storeUrls: Array<StoreUrl>;
    storeFeatures: Array<string>;
    selectedSearchItem?: SearchItem;
    handleUrl: () => Promise<void>;
    resetSearchItem: () => void;
}>;

export const StoreSearchContext = createContext<
    StoreSearchContextValue | undefined
>(undefined);
StoreSearchContext.displayName = "StoreSearchContext";

export const StoreSearchContextProvider = ({
    stores,
    selectedStore,
    storesLoading,
    storeUrls,
    storeFeatures,
    selectedSearchItem,
    handleUrl,
    resetSearchItem,
    children
}: StoreSearchContextProviderProps) => {
    const [tab, setTab] = useState<"list" | "map">("list");

    return (
        <StoreSearchContext
            value={{
                stores: stores,
                storesLoading: storesLoading,
                selectedStore: selectedStore,
                selectedSearchItem: selectedSearchItem,
                storeUrls: storeUrls,
                storeFeatures: [...FIXED_STORE_FEATURES, ...storeFeatures],
                tab: tab,
                toggleSearchParam: toggleSearchParam(handleUrl),
                setSearchParams: setSearchParams(handleUrl),
                resetSearchItem: resetSearchItem,
                setTab: setTab
            }}
        >
            {children}
        </StoreSearchContext>
    );
};

export const useStoreSearchContext = () => {
    const context = use<StoreSearchContextValue | undefined>(
        StoreSearchContext
    );

    if (!isDefined(context)) {
        throw new Error(`${StoreSearchContext.displayName} is undefined.`);
    }

    return context;
};
